<template>
  <modal-brandme :show_modal="show_modal" size="lg" @hidden="$emit('hidden')" :key="show_modal">
    <template #title>
      <h4 class="m-0 pl-1">{{ $t('creator.cropCoverTitle') }}</h4>
    </template>
    <template #body>
      <p class="text-muted px-1">{{ $t('creator.cropCoverText') }}</p>
      <div
        ref="frame"
        :class="['cover-crop-frame', {'is-dragging': dragging}]"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @wheel.prevent="onWheel"
      >
        <img v-if="image_src" :src="image_src" :style="image_style" class="cover-crop-image" draggable="false" alt="" @load="onImageLoad">
        <div class="cover-crop-grid"></div>
      </div>
      <div class="d-flex align-items-center px-1 mt-2">
        <feather-icon icon="ZoomOutIcon" class="text-muted cursor-pointer" @click="setZoom(zoom - 0.2)"></feather-icon>
        <b-form-input
          type="range"
          :value="zoom"
          :min="1"
          :max="MAX_ZOOM"
          step="0.01"
          class="mx-1"
          :aria-label="$t('creator.cropCoverZoom')"
          @input="setZoom(Number($event))"
        ></b-form-input>
        <feather-icon icon="ZoomInIcon" class="text-muted cursor-pointer" @click="setZoom(zoom + 0.2)"></feather-icon>
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-content-end p-1">
        <b-button variant="light" @click="$emit('keep_original')">{{ $t('creator.cropCoverKeep') }}</b-button>
        <b-button variant="blue-button" class="blue-button ml-1" :disabled="!natural_width || saving" @click="save">
          <b-spinner v-if="saving" small class="mr-05"></b-spinner>
          {{ $t('creator.cropCoverSave') }}
        </b-button>
      </div>
    </template>
  </modal-brandme>
</template>

<script>
import { BButton, BFormInput, BSpinner } from 'bootstrap-vue';

const RATIO = 16 / 9;
const MAX_ZOOM = 3;
const MAX_OUTPUT_WIDTH = 1920;

export default {
  name: 'coverCropper',
  components: {
    BButton,
    BFormInput,
    BSpinner,
    modalBrandme: () => import('@/views/components/modal/ModalBrandme.vue'),
  },
  props: {
    show_modal: {
      type: Boolean,
      required: true
    },
    file: {
      type: File,
      required: true
    },
    // Zoom y posición del último recorte guardado, para no empezar desde cero
    initial_state: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      MAX_ZOOM,
      image_src: null,
      natural_width: 0,
      natural_height: 0,
      frame_width: 0,
      zoom: 1,
      // Punto de la imagen original (en px) que queda al centro del marco
      center_x: 0,
      center_y: 0,
      dragging: false,
      last_pointer: null,
      saving: false,
      resize_observer: null,
    }
  },
  computed: {
    frame_height() {
      return this.frame_width / RATIO;
    },
    base_scale() {
      if (!this.natural_width || !this.frame_width) return 1;
      return Math.max(this.frame_width / this.natural_width, this.frame_height / this.natural_height);
    },
    scale() {
      return this.base_scale * this.zoom;
    },
    image_style() {
      const x = (this.frame_width / 2) - (this.center_x * this.scale);
      const y = (this.frame_height / 2) - (this.center_y * this.scale);
      return {
        width: `${this.natural_width * this.scale}px`,
        height: `${this.natural_height * this.scale}px`,
        transform: `translate(${x}px, ${y}px)`,
      };
    },
  },
  created() {
    this.image_src = URL.createObjectURL(this.file);
  },
  mounted() {
    // El contenido de b-modal se monta después de abrirse, se espera a que exista el marco
    this.waitForFrame();
  },
  beforeDestroy() {
    if (this.resize_observer) this.resize_observer.disconnect();
    URL.revokeObjectURL(this.image_src);
  },
  methods: {
    waitForFrame(attempts = 0) {
      if (this.$refs.frame) this.observeFrame(this.$refs.frame);
      else if (attempts < 50) setTimeout(() => this.waitForFrame(attempts + 1), 50);
    },
    observeFrame(frame) {
      if (this.resize_observer) return;
      this.frame_width = frame.clientWidth;
      this.resize_observer = new ResizeObserver(() => {
        this.frame_width = frame.clientWidth;
        this.clampCenter();
      });
      this.resize_observer.observe(frame);
    },
    onImageLoad(event) {
      this.natural_width = event.target.naturalWidth;
      this.natural_height = event.target.naturalHeight;
      if (this.initial_state) {
        this.zoom = this.initial_state.zoom;
        this.center_x = this.initial_state.center_x;
        this.center_y = this.initial_state.center_y;
        this.clampCenter();
      } else {
        this.center_x = this.natural_width / 2;
        this.center_y = this.natural_height / 2;
      }
    },
    clampCenter() {
      if (!this.natural_width || !this.frame_width) return;
      const half_w = this.frame_width / (2 * this.scale);
      const half_h = this.frame_height / (2 * this.scale);
      this.center_x = Math.min(Math.max(this.center_x, half_w), this.natural_width - half_w);
      this.center_y = Math.min(Math.max(this.center_y, half_h), this.natural_height - half_h);
    },
    setZoom(value) {
      this.zoom = Math.min(Math.max(value, 1), MAX_ZOOM);
      this.clampCenter();
    },
    onWheel(event) {
      this.setZoom(this.zoom - (event.deltaY * 0.002));
    },
    onPointerDown(event) {
      this.dragging = true;
      this.last_pointer = { x: event.clientX, y: event.clientY };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove(event) {
      if (!this.dragging) return;
      this.center_x -= (event.clientX - this.last_pointer.x) / this.scale;
      this.center_y -= (event.clientY - this.last_pointer.y) / this.scale;
      this.last_pointer = { x: event.clientX, y: event.clientY };
      this.clampCenter();
    },
    onPointerUp() {
      this.dragging = false;
    },
    save() {
      this.saving = true;
      const source_width = this.frame_width / this.scale;
      const source_height = this.frame_height / this.scale;
      const source_x = this.center_x - (source_width / 2);
      const source_y = this.center_y - (source_height / 2);

      const output_width = Math.round(Math.min(source_width, MAX_OUTPUT_WIDTH));
      const canvas = document.createElement('canvas');
      canvas.width = output_width;
      canvas.height = Math.round(output_width / RATIO);
      const context = canvas.getContext('2d');
      context.imageSmoothingQuality = 'high';
      const image = new Image();
      image.onload = () => {
        context.drawImage(image, source_x, source_y, source_width, source_height, 0, 0, canvas.width, canvas.height);
        const type = this.file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob((blob) => {
          const name = this.file.name.replace(/\.[^.]+$/, type === 'image/png' ? '.png' : '.jpg');
          const state = { zoom: this.zoom, center_x: this.center_x, center_y: this.center_y };
          this.$emit('cropped', new File([blob], name, { type }), state);
          this.saving = false;
        }, type, 0.92);
      };
      image.src = this.image_src;
    },
  }
}
</script>

<style lang="scss" scoped>
.cover-crop-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 0.7em;
  background-color: #DEE2E6;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }
}
.cover-crop-image {
  position: absolute;
  top: 0;
  left: 0;
  max-width: none;
  pointer-events: none;
  transform-origin: 0 0;
}
.cover-crop-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.35) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.35) 1px, transparent 1px);
  background-size: 33.333% 33.333%;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.is-dragging .cover-crop-grid {
  opacity: 1;
}
.mr-05 {
  margin-right: 0.5em;
}
</style>
